import { useEffect } from 'react';
import Header from './components/Header/Header';
import Sidebar from './components/Sidebar/Sidebar';
import Index from './pages/Index/Index';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useNavigate } from 'react-router';
import Signup from './pages/Authentication/Signup';
import Protect from './pages/Authentication/Protect';
import { useLocation } from 'react-router-dom';
import Signin from './pages/Authentication/Signin';
import ForgetPassword from './pages/Authentication/ForgetPassword';
import ResetPassword from './pages/Authentication/ResetPassword';
import { setLoginUser } from './store/userSlice';
import { showToast } from '@jobber/components/Toast';
import InitialSocket from './socket/InitialSocket';
import AdvanceQuotes from './pages/AdvanceQuotes/AdvanceQuotes';
import DetailsPage from './pages/AdvanceQuotes/DetailsPage';
import Settings from './pages/Settings/Settings';
import AdvanceNotes from './pages/AdvanceNotes/AdvanceNotes';
import PrivacyPolicy from './pages/Public/PrivacyPolicy';
import UserAgreement from './pages/Public/UserAgreement';
import NoAccess from './pages/Public/NoAccess';
import AdvanceJobs from './pages/AdvanceJobs/AdvanceJobs';
import ApprovedQuotes from './pages/AdvanceJobs/ApprovedQuotes';
import AdvanceVisits from './pages/AdvanceVisits/AdvanceVisits';
import Dashboard from './pages/Dashboard/Dashboard';
import { AutomationFilterProvider } from './pages/AdvanceJobs/FilterQuery/AutomationFilterQuery';
import { QuotesPreparation } from './pages/QuotePreparation/index';
import DraftQuotePage from './pages/DraftQuotesFix/Page';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { EditQuote } from './pages/AdvanceQuotes/EditQuote';
import { AuthProvider } from './auth-provider';
import { useAuth } from './hooks/use-auth';
import { ROUTE_PERMISSIONS } from './config/permissions';
import VisitsMap from './pages/visits-map/visits-map';
import PageErrorBoundary from './components/Common/PageErrorBoundary';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  }
})

function AppContent() {
  const layout = useSelector((store) => store.layout);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const isLoginOrSignupPage =
    location.pathname === '/login' ||
    location.pathname === '/signup' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password' ||
    location.pathname === '/privacy-policy' ||
    location.pathname === '/user-license-agreement';

  if (isLoginOrSignupPage) {
    document.body.classList.remove('body-pd');
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const userConnection = queryParams.get('userConnection');
    const userQBConnection = queryParams.get('userQBConnection');

    if (isAuthenticated && user) {
      dispatch(setLoginUser({ data: user }));

      if (userConnection === 'true') {
        navigate('/');
        showToast({
          message: 'All tags will be fetched within 15 minutes',
          variation: 'success'
        });
      }
      if (userQBConnection === 'true') {
        navigate('/');
        showToast({
          message: 'Quickbooks connected successfully',
          variation: 'success'
        });
      }
    }
  }, [isAuthenticated, user?._id]);

  return (
    <>
      <AutomationFilterProvider>
        <InitialSocket />
        {!isLoginOrSignupPage && <Header />}
        {layout.showSidebar && !isLoginOrSignupPage ? <Sidebar /> : ''}

        <div style={{ height: 'calc(100vh - (var(--header-height) + 1rem))' }} className={`${!isLoginOrSignupPage ? 'pt-3' : ''} `}>
          <PageErrorBoundary resetKey={location.key} onLeave={() => navigate('/clients')}>
            <Routes>
              <Route index element={<Protect element={<Navigate to={'/clients'} />} />} exact />
              <Route path="/dashboard" element={<Protect element={<Dashboard />} permissions={ROUTE_PERMISSIONS.dashboard} />} />
              <Route path="/clients" element={<Protect element={<Index />} permissions={ROUTE_PERMISSIONS.clients} />} />
              <Route path="/settings" element={<Protect element={<Settings />} permissions={ROUTE_PERMISSIONS.settings} />} />
              <Route path="/recycle-quotes" element={<Protect element={<AdvanceQuotes />} permissions={ROUTE_PERMISSIONS.quotes} />} />
              <Route path="/advance-quotes" element={<Protect element={<AdvanceQuotes />} permissions={ROUTE_PERMISSIONS.quotes} />} />
              <Route path="/advance-quotes/:pageId/:clientId/edit" element={<Protect element={<EditQuote />} permissions={ROUTE_PERMISSIONS.quotesUpdate} />} />
              <Route path="/advance-quotes/:name/:id/:page" element={<Protect element={<DetailsPage />} permissions={ROUTE_PERMISSIONS.quotes} />} />
              <Route path="/advance-notes" element={<Protect element={<AdvanceNotes />} permissions={ROUTE_PERMISSIONS.notes} />} />
              <Route path="/advance-visits" element={<Protect element={<AdvanceVisits />} permissions={ROUTE_PERMISSIONS.visits} />} />
              <Route path="/visits-map" element={<Protect element={<VisitsMap />} permissions={ROUTE_PERMISSIONS.visits} />} />
              <Route path="/quotes-prep" element={<Protect element={<QuotesPreparation />} permissions={ROUTE_PERMISSIONS.quotesPreparation} />} />
              <Route path="/draft-quotes" element={<Protect element={<DraftQuotePage />} permissions={ROUTE_PERMISSIONS.quoteFix} />} />
              <Route path="/job-automation" element={<Protect element={<AdvanceJobs />} permissions={ROUTE_PERMISSIONS.jobAutomation} />} />
              <Route path="/approved-quotes" element={<Protect element={<ApprovedQuotes />} permissions={ROUTE_PERMISSIONS.approvedQuotes} />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/login" element={<Signin />} />
              <Route path="/forgot-password" element={<ForgetPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/user-license-agreement" element={<UserAgreement />} />
              <Route path="/no-access" element={<Protect element={<NoAccess />} />} />
            </Routes>
          </PageErrorBoundary>
        </div>
      </AutomationFilterProvider>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
